import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Store, DollarSign, Calendar, Edit, Eye, FileText, Lock, Unlock, LogIn, Key, Trash2, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { getBackendUrl } from '../../utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const MasterRestaurantsContent = ({ onImpersonate }) => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showExpiryModal, setShowExpiryModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [editingPrice, setEditingPrice] = useState(74.90);
  const [savingPrice, setSavingPrice] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, suspended, expired
  const [impersonating, setImpersonating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Função para salvar preço personalizado
  const handleSavePrice = async () => {
    if (!selectedRestaurant) return;
    
    setSavingPrice(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/master/restaurants/${selectedRestaurant.id}/subscription-price`,
        { custom_subscription_price: editingPrice === '' ? null : parseFloat(editingPrice) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Valor da assinatura atualizado!');
      setShowPriceModal(false);
      fetchRestaurants(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao atualizar preço:', error);
      toast.error('Erro ao atualizar valor');
    } finally {
      setSavingPrice(false);
    }
  };

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const fetchRestaurants = async () => {
    try {
      const response = await axios.get(`${API}/master/restaurants`);
      setRestaurants(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      setLoading(false);
    }
  };

  const toggleSuspend = async (restaurantId, currentStatus) => {
    try {
      await axios.put(`${API}/master/restaurants/${restaurantId}/status`, {
        suspended: !currentStatus
      });
      fetchRestaurants();
    } catch (error) {
      console.error('Error toggling suspend:', error);
      alert('Erro ao atualizar status da loja');
    }
  };

  const updateExpiry = async () => {
    if (!selectedRestaurant || !expiryDate) return;
    
    try {
      await axios.put(`${API}/master/restaurants/${selectedRestaurant.id}/expiry`, {
        expiry_date: expiryDate
      });
      setShowExpiryModal(false);
      setExpiryDate('');
      setSelectedRestaurant(null);
      fetchRestaurants();
    } catch (error) {
      console.error('Error updating expiry:', error);
      alert('Erro ao atualizar data de vencimento');
    }
  };

  const updateNotes = async () => {
    if (!selectedRestaurant) return;
    
    try {
      await axios.put(`${API}/master/restaurants/${selectedRestaurant.id}/notes`, {
        master_notes: notes
      });
      setShowNotesModal(false);
      setNotes('');
      setSelectedRestaurant(null);
      fetchRestaurants();
    } catch (error) {
      console.error('Error updating notes:', error);
      alert('Erro ao atualizar observações');
    }
  };

  const resetPassword = async () => {
    if (!selectedRestaurant || !newPassword) {
      alert('Por favor, preencha a nova senha');
      return;
    }
    
    if (newPassword.length < 6) {
      alert('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    if (!window.confirm(`Deseja realmente redefinir a senha da loja "${selectedRestaurant.name}"?`)) {
      return;
    }
    
    try {
      await axios.put(`${API}/master/restaurants/${selectedRestaurant.id}/password`, {
        new_password: newPassword
      });
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedRestaurant(null);
      alert('Senha redefinida com sucesso!');
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Erro ao redefinir senha: ' + (error.response?.data?.detail || error.message));
    }
  };

  const viewDetails = async (restaurant) => {
    try {
      const response = await axios.get(`${API}/master/restaurants/${restaurant.id}`);
      setSelectedRestaurant(response.data);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching restaurant details:', error);
      alert('Erro ao carregar detalhes');
    }
  };

  const deleteRestaurant = async () => {
    if (!selectedRestaurant) return;
    
    // Verificar se o texto de confirmação está correto
    if (deleteConfirmText !== selectedRestaurant.name) {
      alert('O nome da loja não corresponde. Digite exatamente o nome da loja para confirmar.');
      return;
    }
    
    setDeleting(true);
    
    try {
      const response = await axios.delete(`${API}/master/restaurants/${selectedRestaurant.id}`);
      
      alert(`✅ Loja deletada com sucesso!\n\nItens removidos: ${response.data.total_items_deleted}\n\nTodos os dados foram permanentemente excluídos.`);
      
      setShowDeleteModal(false);
      setDeleteConfirmText('');
      setSelectedRestaurant(null);
      fetchRestaurants();
    } catch (error) {
      console.error('Error deleting restaurant:', error);
      alert('❌ Erro ao deletar loja: ' + (error.response?.data?.detail || error.message));
    } finally {
      setDeleting(false);
    }
  };

  const handleImpersonate = async (restaurant) => {
    if (!onImpersonate) {
      alert('Função de acesso ao painel não disponível');
      return;
    }

    if (!window.confirm(`Deseja acessar o painel da loja "${restaurant.name}"?`)) {
      return;
    }

    try {
      setImpersonating(true);
      const response = await axios.post(`${API}/master/restaurants/${restaurant.id}/impersonate`);
      
      // Call the parent handler to switch to restaurant view
      onImpersonate(response.data);
    } catch (error) {
      console.error('Error impersonating restaurant:', error);
      alert('Erro ao acessar painel da loja: ' + (error.response?.data?.detail || error.message));
      setImpersonating(false);
    }
  };

  const filteredRestaurants = restaurants.filter(r => {
    const matchesSearch = r.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         r.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         r.subdomain.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;

    if (filterStatus === 'all') return true;
    if (filterStatus === 'active') return !r.suspended && !r.is_expired;
    if (filterStatus === 'suspended') return r.suspended;
    if (filterStatus === 'expired') return r.is_expired;
    
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">Gestão de Lojas</h1>
        <p className="text-gray-400 mt-1">{restaurants.length} lojas cadastradas</p>
      </div>

      {/* Filters */}
      <div className="glass-card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Buscar por nome, email ou subdomínio..."
              className="glass-input w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterStatus === 'all' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Todas
            </button>
            <button
              onClick={() => setFilterStatus('active')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterStatus === 'active' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Ativas
            </button>
            <button
              onClick={() => setFilterStatus('suspended')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterStatus === 'suspended' 
                  ? 'bg-red-500 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Suspensas
            </button>
            <button
              onClick={() => setFilterStatus('expired')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterStatus === 'expired' 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              Expiradas
            </button>
          </div>
        </div>
      </div>

      {/* Restaurants Table */}
      <div className="glass-card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left p-4 text-gray-400 font-medium">Loja</th>
              <th className="text-left p-4 text-gray-400 font-medium">Subdomínio</th>
              <th className="text-left p-4 text-gray-400 font-medium">Status</th>
              <th className="text-left p-4 text-gray-400 font-medium">Vencimento</th>
              <th className="text-left p-4 text-gray-400 font-medium">Valor Assinatura</th>
              <th className="text-left p-4 text-gray-400 font-medium">Pedidos</th>
              <th className="text-left p-4 text-gray-400 font-medium">Receita</th>
              <th className="text-right p-4 text-gray-400 font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredRestaurants.map((restaurant) => (
              <tr key={restaurant.id} className="border-b border-gray-800 hover:bg-gray-800/30">
                <td className="p-4">
                  <div>
                    <p className="text-white font-medium">{restaurant.name}</p>
                    <p className="text-sm text-gray-400">{restaurant.email}</p>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-gray-300">{restaurant.subdomain}</span>
                </td>
                <td className="p-4">
                  {restaurant.suspended ? (
                    <span className="px-2 py-1 rounded-full text-xs bg-red-500/20 text-red-400">
                      Suspensa
                    </span>
                  ) : restaurant.is_expired ? (
                    <span className="px-2 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400">
                      Expirada
                    </span>
                  ) : (
                    <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">
                      Ativa
                    </span>
                  )}
                </td>
                <td className="p-4">
                  {restaurant.expiry_date ? (
                    <div>
                      <p className="text-gray-300 text-sm">
                        {new Date(restaurant.expiry_date).toLocaleDateString('pt-BR')}
                      </p>
                      {restaurant.days_until_expiry !== null && (
                        <p className={`text-xs ${
                          restaurant.days_until_expiry < 0 
                            ? 'text-red-400' 
                            : restaurant.days_until_expiry < 7 
                            ? 'text-yellow-400' 
                            : 'text-gray-500'
                        }`}>
                          {restaurant.days_until_expiry < 0 
                            ? `Expirou há ${Math.abs(restaurant.days_until_expiry)} dias` 
                            : `${restaurant.days_until_expiry} dias restantes`}
                        </p>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-500 text-sm">Sem vencimento</span>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${restaurant.is_custom_price ? 'text-yellow-400' : 'text-gray-300'}`}>
                      R$ {(restaurant.subscription_price || 74.90).toFixed(2)}
                    </span>
                    {restaurant.is_custom_price && (
                      <span className="text-xs bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded">
                        Custom
                      </span>
                    )}
                    <button
                      onClick={() => {
                        setSelectedRestaurant(restaurant);
                        setEditingPrice(restaurant.subscription_price || 74.90);
                        setShowPriceModal(true);
                      }}
                      className="p-1 text-gray-400 hover:text-yellow-400 hover:bg-yellow-400/10 rounded transition-colors"
                      title="Editar valor da assinatura"
                    >
                      <Edit size={14} />
                    </button>
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-gray-300">{restaurant.stats?.orders || 0}</span>
                </td>
                <td className="p-4">
                  <span className="text-green-400 font-medium">
                    R$ {restaurant.stats?.revenue?.toFixed(2) || '0.00'}
                  </span>
                </td>
                <td className="p-4">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => handleImpersonate(restaurant)}
                      disabled={impersonating}
                      className="p-2 text-purple-400 hover:bg-purple-400/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Acessar Painel da Loja"
                      data-testid={`impersonate-${restaurant.id}`}
                    >
                      {impersonating ? (
                        <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <LogIn size={18} />
                      )}
                    </button>
                    <button
                      onClick={() => viewDetails(restaurant)}
                      className="p-2 text-blue-400 hover:bg-blue-400/20 rounded-lg transition-colors"
                      title="Ver detalhes"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRestaurant(restaurant);
                        setNotes(restaurant.master_notes || '');
                        setShowNotesModal(true);
                      }}
                      className="p-2 text-cyan-400 hover:bg-cyan-400/20 rounded-lg transition-colors"
                      title="Editar observações"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRestaurant(restaurant);
                        setNewPassword('');
                        setShowPasswordModal(true);
                      }}
                      className="p-2 text-orange-400 hover:bg-orange-400/20 rounded-lg transition-colors"
                      title="Redefinir senha"
                      data-testid={`reset-password-${restaurant.id}`}
                    >
                      <Key size={18} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRestaurant(restaurant);
                        setExpiryDate(restaurant.expiry_date ? restaurant.expiry_date.split('T')[0] : '');
                        setShowExpiryModal(true);
                      }}
                      className="p-2 text-yellow-400 hover:bg-yellow-400/20 rounded-lg transition-colors"
                      title="Definir vencimento"
                    >
                      <Calendar size={18} />
                    </button>
                    <button
                      onClick={() => toggleSuspend(restaurant.id, restaurant.suspended)}
                      className={`p-2 rounded-lg transition-colors ${
                        restaurant.suspended
                          ? 'text-green-400 hover:bg-green-400/20'
                          : 'text-red-400 hover:bg-red-400/20'
                      }`}
                      title={restaurant.suspended ? 'Ativar' : 'Suspender'}
                    >
                      {restaurant.suspended ? <Unlock size={18} /> : <Lock size={18} />}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedRestaurant(restaurant);
                        setDeleteConfirmText('');
                        setShowDeleteModal(true);
                      }}
                      className="p-2 text-red-500 hover:bg-red-500/20 rounded-lg transition-colors"
                      title="Deletar loja permanentemente"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}

          </tbody>
        </table>
        
        {filteredRestaurants.length === 0 && (
          <div className="text-center py-12">
            <Store size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-400">Nenhuma loja encontrada</p>
          </div>
        )}
      </div>

      {/* Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="glass-modal max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              Detalhes da Loja: {selectedRestaurant?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRestaurant && (
            <div className="space-y-6">
              {/* Master Notes */}
              {selectedRestaurant.master_notes && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <FileText className="w-5 h-5 text-cyan-400 mr-2" />
                    <h3 className="text-lg font-semibold text-white">Observações</h3>
                  </div>
                  <p className="text-gray-300 whitespace-pre-wrap">{selectedRestaurant.master_notes}</p>
                </div>
              )}
              
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400">Email</label>
                  <p className="text-white">{selectedRestaurant.email}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Subdomínio</label>
                  <p className="text-white">{selectedRestaurant.subdomain}</p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Criado em</label>
                  <p className="text-white">
                    {new Date(selectedRestaurant.created_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-gray-400">Status</label>
                  <p className="text-white">
                    {selectedRestaurant.suspended ? '🔴 Suspensa' : 
                     selectedRestaurant.is_expired ? '🟣 Expirada' : '🟢 Ativa'}
                  </p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-800/50 p-4 rounded-lg">
                  <p className="text-gray-400 text-sm">Pedidos</p>
                  <p className="text-2xl font-bold text-white">
                    {selectedRestaurant.detailed_stats?.orders?.total || 0}
                  </p>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-lg">
                  <p className="text-gray-400 text-sm">Produtos</p>
                  <p className="text-2xl font-bold text-white">
                    {selectedRestaurant.detailed_stats?.products?.total || 0}
                  </p>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-lg">
                  <p className="text-gray-400 text-sm">Categorias</p>
                  <p className="text-2xl font-bold text-white">
                    {selectedRestaurant.detailed_stats?.categories || 0}
                  </p>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-lg">
                  <p className="text-gray-400 text-sm">Receita Total</p>
                  <p className="text-2xl font-bold text-green-400">
                    R$ {selectedRestaurant.detailed_stats?.revenue?.total?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>

              {/* Recent Orders */}
              {selectedRestaurant.detailed_stats?.orders?.recent && 
               selectedRestaurant.detailed_stats.orders.recent.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Pedidos Recentes</h3>
                  <div className="space-y-2">
                    {selectedRestaurant.detailed_stats.orders.recent.slice(0, 5).map((order) => (
                      <div key={order.id} className="flex justify-between items-center bg-gray-800/50 p-3 rounded-lg">
                        <div>
                          <p className="text-white font-medium">#{order.order_number}</p>
                          <p className="text-sm text-gray-400">{order.customer_name || 'Cliente'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-green-400 font-medium">R$ {order.total?.toFixed(2)}</p>
                          <p className="text-xs text-gray-500">{order.status}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Expiry Modal */}
      <Dialog open={showExpiryModal} onOpenChange={setShowExpiryModal}>
        <DialogContent className="glass-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              Definir Data de Vencimento
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Loja: {selectedRestaurant?.name}
              </label>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Data de Vencimento
              </label>
              <input
                type="date"
                className="glass-input w-full"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Deixe vazio para remover a data de vencimento
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowExpiryModal(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={updateExpiry}
                className="glass-button-primary"
              >
                Salvar
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notes Modal */}
      <Dialog open={showNotesModal} onOpenChange={setShowNotesModal}>
        <DialogContent className="glass-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              Observações da Loja
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Loja: {selectedRestaurant?.name}
              </label>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Observações Internas
              </label>
              <textarea
                className="glass-input w-full resize-none"
                rows={6}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Adicione observações, anotações ou lembretes sobre esta loja..."
              />
              <p className="text-xs text-gray-500 mt-1">
                Estas notas são visíveis apenas para o master admin
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={updateNotes}
                className="glass-button-primary"
              >
                Salvar
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="glass-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5 text-orange-400" />
              Redefinir Senha da Loja
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
              <p className="text-sm text-orange-300">
                <strong>Atenção:</strong> Você está prestes a redefinir a senha de acesso ao painel desta loja.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Loja: <span className="text-white font-semibold">{selectedRestaurant?.name}</span>
              </label>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nova Senha <span className="text-red-400">*</span>
              </label>
              <input
                type="password"
                className="glass-input w-full"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Digite a nova senha (mínimo 6 caracteres)"
                minLength={6}
                data-testid="new-password-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                A senha deve ter no mínimo 6 caracteres
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setNewPassword('');
                  setSelectedRestaurant(null);
                }}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={resetPassword}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors"
                disabled={!newPassword || newPassword.length < 6}
                data-testid="confirm-reset-password"
              >
                Redefinir Senha
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="glass-modal max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white flex items-center gap-2">
              <Trash2 className="text-red-500" size={28} />
              Deletar Loja Permanentemente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-red-500/10 border-2 border-red-500 rounded-lg">
              <p className="text-red-400 font-semibold mb-2">⚠️ ATENÇÃO: Esta ação é IRREVERSÍVEL!</p>
              <p className="text-gray-300 text-sm mb-2">Ao deletar esta loja, os seguintes dados serão PERMANENTEMENTE excluídos:</p>
              <ul className="text-gray-400 text-sm space-y-1 ml-4">
                <li>• Todos os produtos e categorias</li>
                <li>• Todos os adicionais e grupos de adicionais</li>
                <li>• Todos os pedidos e histórico</li>
                <li>• Todas as mesas e configurações</li>
                <li>• Todos os cupons e zonas de entrega</li>
                <li>• Todos os usuários e clientes</li>
                <li>• Todos os logs e configurações</li>
              </ul>
            </div>

            {selectedRestaurant && (
              <div>
                <p className="text-gray-300 mb-2">
                  Para confirmar, digite o nome da loja exatamente como aparece abaixo:
                </p>
                <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700 mb-3">
                  <p className="text-white font-bold text-center">{selectedRestaurant.name}</p>
                </div>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Digite o nome da loja aqui"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-red-500 focus:outline-none"
                  autoFocus
                />
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText('');
                }}
                disabled={deleting}
                className="flex-1 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={deleteRestaurant}
                disabled={deleting || deleteConfirmText !== selectedRestaurant?.name}
                className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {deleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Deletando...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Deletar Permanentemente
                  </>
                )}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Price Edit Modal */}
      <Dialog open={showPriceModal} onOpenChange={setShowPriceModal}>
        <DialogContent className="glass-modal max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
              <DollarSign className="text-yellow-400" size={24} />
              Valor da Assinatura
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {selectedRestaurant && (
              <>
                <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                  <p className="text-sm text-gray-400">Loja:</p>
                  <p className="text-white font-semibold">{selectedRestaurant.name}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Valor Mensal (R$)
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">R$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingPrice}
                      onChange={(e) => setEditingPrice(e.target.value)}
                      placeholder="74.90"
                      className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-lg font-semibold focus:border-yellow-500 focus:outline-none"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Deixe vazio ou 0 para usar o valor padrão global
                  </p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowPriceModal(false)}
                    disabled={savingPrice}
                    className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => {
                      setEditingPrice('');
                      handleSavePrice();
                    }}
                    disabled={savingPrice}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                  >
                    Usar Padrão
                  </button>
                  <button
                    onClick={handleSavePrice}
                    disabled={savingPrice}
                    className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {savingPrice ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        <Check size={16} />
                        Salvar
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ============================================================================
// MASTER ANALYTICS CONTENT (Gráficos e Métricas Avançadas)
// ============================================================================

